import { Icon, Switch } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiInput from "components/base/UiInput";
import UiTypography from "components/base/UiTypography";
import { WorksheetPricingMode } from "generated/graphql";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { toDollars } from "utils/money/toDollars";
import { restorePricingBaseline } from "./worksheetFormValues";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import { WorksheetTotals } from "./useWorksheetTotals";
import { worksheetPricingStatus } from "./worksheetPricing";

type WorksheetCostSummaryProps = {
  showCosts: boolean;
  isReadOnly?: boolean;
  totals: WorksheetTotals;
  showExpenseInputs?: boolean;
  onToggleCosts: () => void;
};

export default function WorksheetCostSummary({
  showCosts,
  isReadOnly,
  totals,
  showExpenseInputs = true,
  onToggleCosts,
}: WorksheetCostSummaryProps) {
  const { control, setValue } = useFormContext<WorksheetItemInput>();
  const values = useWatch({ control }) as WorksheetItemInput;
  const { total, enteredCosts, marginPercentage } = totals;
  const isCostBased = values.pricingMode === WorksheetPricingMode.COST_BASED;

  const handleCostBasedToggle = (enabled: boolean) => {
    setValue("pricingOverrideField", "none", { shouldDirty: true });

    if (enabled) {
      if (values.costRows.length === 0) {
        setValue("fixedTotal", toDollars(total), { shouldDirty: true });
      }
      setValue("pricingMode", WorksheetPricingMode.COST_BASED, { shouldDirty: true });
      return;
    }

    setValue("fixedTotal", toDollars(total), { shouldDirty: true });
    setValue("pricingMode", WorksheetPricingMode.FIXED, { shouldDirty: true });
  };

  const applyPatch = (patch: Partial<WorksheetItemInput>) => {
    (Object.keys(patch) as Array<keyof WorksheetItemInput>).forEach((key) => {
      setValue(key, patch[key] as never, { shouldDirty: true });
    });
  };

  const handleResetPricing = () => {
    if (!values.pricingBaseline) {
      return;
    }

    applyPatch(restorePricingBaseline(values.pricingBaseline));
  };

  const enteredCostsLabel = enteredCosts === null ? "unavailable" : formatCentsToUSD(enteredCosts);
  const isNegativeMargin = marginPercentage !== null && marginPercentage < 0;
  const marginLabel = marginPercentage === null ? "unavailable" : `${marginPercentage}%`;
  const pricingStatus = worksheetPricingStatus({
    pricingMode: values.pricingMode,
    pricingOverrideField: values.pricingOverrideField,
    profitCents: totals.profitCents,
    marginPercentage: totals.marginPercentage,
  });

  return (
    <MDBox display="flex" flexDirection="column" gap="2px">
      <MDBox display="flex" alignItems="center" justifyContent="space-between">
        <UiButton uiVariant="outlined-small" onClick={onToggleCosts}>
          <Icon fontSize="small">{showCosts ? "visibility_off" : "visibility"}</Icon>
          {showCosts ? "Hide Costs" : "Show Costs"}
        </UiButton>

        <MDBox display="flex" alignItems="center" gap="12px">
          <UiTypography uiVariant="body-sm">
            Entered Costs: <b>{enteredCostsLabel}</b>
          </UiTypography>
          <UiTypography
            uiVariant="body-sm"
            sx={{ color: isNegativeMargin ? draftColors.red : "inherit" }}
          >
            Estimated Margin: <b>{marginLabel}</b>
          </UiTypography>
          <MDBox display="flex" alignItems="center" gap="4px">
            <UiTypography uiVariant="body-sm">Markup</UiTypography>
            <MDBox sx={{ width: 70 }}>
              <Controller
                control={control}
                name="markupPercentage"
                render={({ field }) => (
                  <UiInput
                    value={field.value}
                    readOnly={isReadOnly}
                    textAlign="right"
                    placeholder="0"
                    onChange={(event) => field.onChange(event.target.value)}
                  />
                )}
              />
            </MDBox>
          </MDBox>
          {showExpenseInputs && (
            <>
              <MDBox display="flex" alignItems="center" gap="4px">
                <UiTypography uiVariant="body-sm">Overhead</UiTypography>
                <MDBox sx={{ width: 70 }}>
                  <Controller
                    control={control}
                    name="overheadPercentage"
                    render={({ field }) => (
                      <UiInput
                        value={field.value}
                        readOnly={isReadOnly}
                        textAlign="right"
                        placeholder="0"
                        onChange={(event) => field.onChange(event.target.value)}
                      />
                    )}
                  />
                </MDBox>
              </MDBox>
              <MDBox display="flex" alignItems="center" gap="4px">
                <UiTypography uiVariant="body-sm">Indirect</UiTypography>
                <MDBox sx={{ width: 70 }}>
                  <Controller
                    control={control}
                    name="indirectPercentage"
                    render={({ field }) => (
                      <UiInput
                        value={field.value}
                        readOnly={isReadOnly}
                        textAlign="right"
                        placeholder="0"
                        onChange={(event) => field.onChange(event.target.value)}
                      />
                    )}
                  />
                </MDBox>
              </MDBox>
            </>
          )}
          <MDBox display="flex" alignItems="center" gap="4px">
            <UiTypography uiVariant="body-sm">Calculate selling price from costs</UiTypography>
            <Switch
              size="small"
              checked={isCostBased}
              disabled={isReadOnly}
              onChange={(event) => handleCostBasedToggle(event.target.checked)}
            />
          </MDBox>
          {values.pricingBaseline && (
            <UiButton uiVariant="outlined-small" onClick={handleResetPricing}>
              <Icon fontSize="small">restart_alt</Icon>
              Reset pricing
            </UiButton>
          )}
        </MDBox>
      </MDBox>
      <UiTypography uiVariant="body-sm" sx={{ color: draftColors.textSecondary, pl: "2px" }}>
        {pricingStatus}
      </UiTypography>
    </MDBox>
  );
}
